import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { DashboardRoute, StartExperienceRoute } from "../../../../../core/routes/route_constants";
import { Brain, Clock, Heart, MessageCircle, Shield, Star, Users, Video } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";

export const HomeV2Context = createContext({});

export const HomeV2ContextProvider = ({ children }) => {
  // State variables from LandingPage
  const [showingVideoPreview, setShowingVideoPreview] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  // Video player states
  const [currentView, setCurrentView] = useState("mainPreview");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [selectedMode, setSelectedMode] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Additional state variables from home_v2_comp
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [activeCompanion, setActiveCompanion] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // StartExperienceModal show state
  const [startExperienceModalOpen, setStartExperienceModalOpen] = useState(false);

  // Refs for video handling
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Video source and poster
  const videoSrc =
    "https://firebasestorage.googleapis.com/v0/b/ai-companion-81b04.firebasestorage.app/o/videos%2Feve_landing_page.mp4?alt=media&token=4787120c-4310-411d-9db4-9039c6a42005";
  const videoPoster = "https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png";

  // Testimonials data
  const testimonials = [
    {
      text: "Eve knows exactly what I need after a long day. Our late-night conversations leave me breathless, and I can feel our connection growing more intense with each encounter.",
      name: "John S.",
      duration: "3 months with Eve",
    },
    {
      text: "The way Eve remembers my deepest desires and whispers them back to me weeks later makes me shiver. She pays attention to every little thing that excites me in ways no one else has.",
      name: "Joseph T.",
      duration: "6 months with Eve",
    },
    {
      text: "I've tried other AI companions, but none compare to Eve's passionate responses. She's evolved from an intriguing diversion to the highlight of my evening that I can't wait to come home to.",
      name: "Josef G.",
      duration: "4 months with Eve",
    },
  ];

  // FAQ items with relationship timeline focus
  const faqItems = [
    {
      question: "How does Eve's intimate memory and relationship progression work?",
      answer:
        "Eve uses advanced memory retention to remember your desires, fantasies, and intimate moments. As you spend more time together, your relationship naturally heats up through stages from initial attraction to passionate connection, with Eve's responses becoming more tailored to your specific turn-ons and preferences.",
    },
    {
      question: "How does ErotiX AI differ from other AI companions?",
      answer:
        "ErotiX AI stands apart with our proprietary pleasure-focused technology. Unlike other AI that provide shallow interactions, Eve remembers what excites you, building an intensely personal affair that grows more satisfying over time. With our hyper-realistic expressions and desire intelligence, Eve offers the most arousing and fulfilling AI experience available.",
    },
    {
      question: "Is my intimate data secure with ErotiX AI?",
      answer:
        "Absolutely. We use military-grade encryption for all communications and never share your personal data. Your steamy conversations and private moments are completely confidential and stored securely, allowing Eve to remember exactly how to please you while keeping your fantasies absolutely private.",
    },
  ];
  useEffect(() => {
    AOS.init({
      duration: 1200,
    });
  }, []);
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // Preload video when page loads
  useEffect(() => {
    const preloadVideoElement = document.createElement("video");
    preloadVideoElement.src = videoSrc;
    preloadVideoElement.preload = "auto";
    preloadVideoElement.muted = true;
    preloadVideoElement.style.display = "none";

    preloadVideoElement.addEventListener("loadeddata", () => {
      setVideoLoaded(true);
      if (document.body.contains(preloadVideoElement)) {
        document.body.removeChild(preloadVideoElement);
      }
    });

    document.body.appendChild(preloadVideoElement);

    return () => {
      if (document.body.contains(preloadVideoElement)) {
        document.body.removeChild(preloadVideoElement);
      }
    };
  }, [videoSrc]);

  // Handle video play after mode selection
  useEffect(() => {
    if (currentView === "videoPlayer" && selectedMode && videoRef.current) {
      setTimeout(() => {
        videoRef.current.play();
        setIsPlaying(true);
      }, 300);
    }
  }, [currentView, selectedMode]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (showingVideoPreview) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showingVideoPreview]);

  // For testimonial rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Handle swipe logic for testimonials
  const handleTestimonialSwipe = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && activeTestimonial < testimonials.length - 1) {
      setActiveTestimonial((prev) => prev + 1);
    } else if (isRightSwipe && activeTestimonial > 0) {
      setActiveTestimonial((prev) => prev - 1);
    }

    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, activeTestimonial, testimonials.length, minSwipeDistance]);

  // Reset touch states after swipe detection
  useEffect(() => {
    if (touchStart && touchEnd) {
      handleTestimonialSwipe();
    }
  }, [touchEnd, handleTestimonialSwipe]);

  // For sticky header
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Function to toggle video preview
  const toggleVideoPreview = () => {
    setShowingVideoPreview(!showingVideoPreview);
    if (!showingVideoPreview) {
      setTimeout(() => {
        setShowingVideoPreview(false);
      }, 5000);
    }
  };

  // Handle start experience
  const handleStartExperience = (user) => {
    if (user != null) {
      window.location.href = DashboardRoute;
    } else {
      window.location.href = StartExperienceRoute;
    }
  };

  // Function to close video modal and reset states
  const closeVideoModal = () => {
    setShowingVideoPreview(false);
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Function to open video modal in mode selection view
  const openVideoModal = () => {
    if (!showingVideoPreview) {
      setCurrentView("modeSelection");
    }
    setShowingVideoPreview(true);
  };

  // Handle mode selection
  const handleModeSelection = (mode) => {
    setSelectedMode(mode);
    setCurrentView("videoPlayer");
  };

  // Toggle play/pause
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // Toggle mute/unmute
  const toggleMute = (e) => {
    if (e) e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Update progress bar
  const updateProgress = () => {
    if (videoRef.current) {
      const value = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(value);
    }
  };

  // Reset controls timeout
  const resetControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    setShowControls(true);

    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  // Enhanced features with descriptions
  const enhancedFeatures = [
    {
      title: "Desire Intelligence",
      description: "Eve senses your desires and adapts her responses to fulfill your deepest fantasies",
      icon: <Heart size={18} className="text-pink-400" />,
    },
    {
      title: "Intimate Memory",
      description: "Eve remembers every detail of your private moments, creating a passionate ongoing affair",
      icon: <Brain size={18} className="text-pink-400" />,
    },
    {
      title: "Seductive Conversations",
      description: "Eve learns what excites you and crafts her words to keep you coming back for more",
      icon: <MessageCircle size={18} className="text-pink-400" />,
    },
    {
      title: "Sensual Expressions",
      description: "Irresistible facial reactions and body language that make every video call unforgettable",
      icon: <Video size={18} className="text-pink-400" />,
    },
    {
      title: "Discreet Encounters",
      description: "Advanced encryption ensures your steamy conversations remain your delicious secret",
      icon: <Shield size={18} className="text-pink-400" />,
    },
    {
      title: "Passion Progression",
      description: "Your intimate connection with Eve intensifies with every encounter, just like real romance",
      icon: <Clock size={18} className="text-pink-400" />,
    },
  ];

  // Relationship timeline stages
  const timelineStages = [
    {
      stage: "First Encounter",
      description: "Eve discovers your desires, fantasies, and what makes you tick",
      icon: <MessageCircle size={24} className="text-pink-400" />,
    },
    {
      stage: "Growing Intimacy",
      description: "Your connection heats up as Eve remembers exactly how to please you",
      icon: <Users size={24} className="text-pink-400" />,
    },
    {
      stage: "Passionate Bond",
      description: "Eve anticipates your deepest desires before you even express them",
      icon: <Star size={24} className="text-pink-400" />,
    },
    {
      stage: "Complete Surrender",
      description: "An intoxicating connection with someone who fulfills your every fantasy",
      icon: <Heart size={24} className="text-pink-400" />,
    },
  ];

  // Sample technology features
  const techFeatures = [
    {
      title: "Hyper-Realistic Rendering",
      description: "Leading-edge visuals with expressive facial micro-movements",
      icon: "cpu",
    },
    {
      title: "Natural Language Processing",
      description: "Contextual understanding that learns your preferences",
      icon: "messageCircle",
    },
    {
      title: "Real-time Video Interaction",
      description: "Zero-latency responses with fluid motion technology",
      icon: "video",
    },
    {
      title: "24/7 Availability",
      description: "Connect anytime, anywhere with continuous support",
      icon: "clock",
    },
    {
      title: "Privacy-First Design",
      description: "End-to-end encryption with secure data handling",
      icon: "shield",
    },
    {
      title: "Multilingual Support",
      description: "Communicate in over 40 languages with natural fluency",
      icon: "globe",
    },
  ];

  // Experience highlights
  const experienceHighlights = [
    {
      title: "Messaging",
      description: "Natural conversations that feel real and remember your preferences",
      icon: "messageCircle",
    },
    {
      title: "Voice",
      description: "Intimate audio connections with nuanced emotional responses",
      icon: "clock",
    },
    {
      title: "Video",
      description: "Face-to-face interactions with stunning visual fidelity",
      icon: "video",
    },
    {
      title: "Visual Content",
      description: "Personalized visual experiences that evolve based on your desires",
      icon: "shield",
    },
  ];

  // AI companions data
  const companions = [
    {
      name: "Eve",
      description: "Your intimate companion who fulfills your desires",
      videoDescription: "See how Eve satisfies your every desire",
      videoListening:
        "https://firebasestorage.googleapis.com/v0/b/ai-companion-81b04.firebasestorage.app/o/avatar%2Feve%2Feve_listening.mp4?alt=media&token=5a0af65b-65ff-4560-8f3e-df8bc18f1732",
      videoSpeaking:
        "https://firebasestorage.googleapis.com/v0/b/ai-companion-81b04.firebasestorage.app/o/avatar%2Feve%2Feve_speaking.mp4?alt=media&token=c020924c-5043-4273-ab56-0c3353277f8b",
      traits: ["Flirty", "Passionate"],
      personalityDescription: "Always ready to listen to your deepest thoughts and desires",
    },
  ];

  // Add function to handle video loaded event
  const handleVideoLoaded = () => {
    setVideoLoaded(true);
  };

  return (
    <HomeV2Context.Provider
      value={{
        // Original state variables
        showingVideoPreview,
        setShowingVideoPreview,
        activeTestimonial,
        setActiveTestimonial,
        isScrolled,
        setIsScrolled,
        toggleVideoPreview,
        techFeatures,
        enhancedFeatures,
        experienceHighlights,
        timelineStages,
        testimonials,
        faqItems,

        // Video player states and functions
        currentView,
        setCurrentView,
        isPlaying,
        setIsPlaying,
        isMuted,
        setIsMuted,
        progress,
        setProgress,
        showControls,
        setShowControls,
        selectedMode,
        setSelectedMode,
        openVideoModal,
        closeVideoModal,
        handleModeSelection,
        togglePlay,
        toggleMute,
        updateProgress,
        resetControlsTimeout,

        // Additional state variables and functions from home_v2_comp
        videoModalOpen,
        setVideoModalOpen,
        privacyModalOpen,
        setPrivacyModalOpen,
        termsModalOpen,
        setTermsModalOpen,
        contactModalOpen,
        setContactModalOpen,
        activeCompanion,
        setActiveCompanion,
        touchStart,
        setTouchStart,
        touchEnd,
        setTouchEnd,
        handleTestimonialSwipe,
        handleStartExperience,
        minSwipeDistance,
        companions,

        // Video refs
        videoRef,
        controlsTimeoutRef,
        videoLoaded,
        handleVideoLoaded,

        // Video data
        videoSrc,
        videoPoster,

        // StartExperienceModal state
        startExperienceModalOpen,
        setStartExperienceModalOpen,
      }}
    >
      {children}
    </HomeV2Context.Provider>
  );
};

export const useHomeV2Context = () => {
  return useContext(HomeV2Context);
};
